import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { FaEye, FaPrint, FaFileExcel, FaFilePdf, FaTimes } from 'react-icons/fa';
import { getCashFlowStatement, clearCashFlowStatement, clearError, clearSuccess } from '../../../../redux/Slices/AccountingReportsSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from './components/PdfPreviewModal';
import { calculatePdfPagination } from '../../../../shared/utils.js/utils';
import { convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';

// cash flow statement component
const CashFlowStatement = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const { cashFlowStatement, isLoading, error, success } = useSelector((state) => state.accountingReports);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation

  useEffect(() => {
    setTitle('accountingReports.cashFlowStatementTitle', { defaultValue: 'قائمة التدفقات النقدية' });
    dispatch(getCashFlowStatement({}));
    return () => {
      dispatch(clearCashFlowStatement());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      if (isFilterApplied) {
        toast.success(t(success) || success, { rtl: isRTL });
      }
      dispatch(clearSuccess());
      setIsFilterApplied(false);
    }
  }, [success, dispatch, isRTL, t, isFilterApplied]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsFilterApplied(true);
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    dispatch(getCashFlowStatement(params));
  };

  const buildDynamicPdfChunks = (sectionName, sectionItems) => {
    const topHeader = document.getElementById(`cash-flow-statement-pdf-measure-${sectionName}-top-header`);
    const thead = document.getElementById(`cash-flow-statement-pdf-measure-${sectionName}-thead`);
    const footer = document.getElementById('cash-flow-statement-pdf-measure-footer');
    const tbody = document.getElementById(`cash-flow-statement-pdf-measure-${sectionName}-tbody`);
    return calculatePdfPagination(sectionItems, { topHeader, thead, footer, tbody }, { safeMargin: 25 });
  };

  const handlePreviewAll = () => {
    if (!cashFlowStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    const chunks = preparePdfChunks();
    setPdfChunks(chunks);
    setIsPreviewOpen(true);
    setPreviewZoom(1.0);
    setPreviewPosition({ x: 0, y: 0 });
  };

  const preparePdfChunks = () => {
    const pages = [];
    const operatingChunks = operatingDetails.length > 0 ? buildDynamicPdfChunks('operating', operatingDetails) : [];
    const financingChunks = financingDetails.length > 0 ? buildDynamicPdfChunks('financing', financingDetails) : [];
    const investingChunks = investingDetails.length > 0 ? buildDynamicPdfChunks('investing', investingDetails) : [];
    const otherChunks = otherActivities.length > 0 ? buildDynamicPdfChunks('other', otherActivities) : [];

    if (operatingChunks.length > 0) {
      pages.push({
        pageNumber: 1,
        showSummary: true,
        operating: operatingChunks[0].items,
        isOperatingLastChunk: operatingChunks.length === 1,
        netOperating: operatingChunks.length === 1 ? netOperating : null,
        financing: null,
        investing: null,
        other: null
      });
    } else {
      pages.push({
        pageNumber: 1,
        showSummary: true,
        operating: null,
        isOperatingLastChunk: false,
        netOperating: null,
        financing: null,
        investing: null,
        other: null
      });
    }

    for (let i = 1; i < operatingChunks.length; i++) {
      pages.push({
        pageNumber: pages.length + 1,
        showSummary: false,
        operating: operatingChunks[i].items,
        isOperatingLastChunk: i === operatingChunks.length - 1,
        netOperating: i === operatingChunks.length - 1 ? netOperating : null,
        financing: null,
        investing: null,
        other: null
      });
    }

    for (let i = 0; i < financingChunks.length; i++) {
      pages.push({
        pageNumber: pages.length + 1,
        showSummary: false,
        operating: null,
        isOperatingLastChunk: false,
        netOperating: null,
        financing: financingChunks[i].items,
        isFinancingLastChunk: i === financingChunks.length - 1,
        netFinancing: i === financingChunks.length - 1 ? netFinancing : null,
        investing: null,
        other: null
      });
    }

    for (let i = 0; i < investingChunks.length; i++) {
      pages.push({
        pageNumber: pages.length + 1,
        showSummary: false,
        operating: null,
        isOperatingLastChunk: false,
        netOperating: null,
        financing: null,
        investing: investingChunks[i].items,
        isInvestingLastChunk: i === investingChunks.length - 1,
        netInvesting: i === investingChunks.length - 1 ? netInvesting : null,
        other: null
      });
    }

    for (let i = 0; i < otherChunks.length; i++) {
      pages.push({
        pageNumber: pages.length + 1,
        showSummary: false,
        operating: null,
        isOperatingLastChunk: false,
        netOperating: null,
        financing: null,
        investing: null,
        other: otherChunks[i].items,
        isOtherLastChunk: i === otherChunks.length - 1,
        netOther: null
      });
    }

    return pages;
  };

  const handlePrintAll = async () => {
    if (!cashFlowStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setPdfType('all');

      const chunks = preparePdfChunks();
      setPdfChunks(chunks);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) => `cash-flow-statement-pdf-all-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_CashFlowStatement_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('accountingReports.printed', { defaultValue: 'تم طباعة التقرير بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(t('accountingReports.printError', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!cashFlowStatement) {
      toast.error(t('accountingReports.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    try {
      const data = cashFlowStatement?.data || cashFlowStatement || {};

      const excelRows = [
        {
          [t('accountingReports.period')]: `${formatDate(meta.start_date || startDate)} - ${formatDate(meta.end_date || endDate)}`,
        },
        {},
        {
          [t('accountingReports.openingCash')]: formatNumber(openingCash),
          [t('accountingReports.netCashFlow')]: formatNumber(netChange),
          [t('accountingReports.closingCash')]: formatNumber(closingCash),
        },
      ];

      const pushSection = (title, rows) => {
        if (!rows || rows.length === 0) return;
        excelRows.push({});
        excelRows.push({ [t('accountingReports.section')]: title });
        excelRows.push({
          [t('accountingReports.description')]: t('accountingReports.description'),
          [t('accountingReports.amount')]: t('accountingReports.amount'),
        });
        rows.forEach((item) => {
          excelRows.push({
            [t('accountingReports.description')]: item.description || item.name || item.title || '—',
            [t('accountingReports.amount')]: formatNumber(item.amount ?? item.value ?? item.total ?? 0),
          });
        });
      };

      pushSection(t('accountingReports.operatingActivities'), operatingDetails);
      if (Number.isFinite(netOperating)) {
        excelRows.push({
          [t('accountingReports.description')]: t('accountingReports.netCashFlow'),
          [t('accountingReports.amount')]: formatNumber(netOperating),
        });
      }
      pushSection(t('accountingReports.investingActivities'), investingDetails);
      if (Number.isFinite(netInvesting)) {
        excelRows.push({
          [t('accountingReports.description')]: t('accountingReports.netCashFlow'),
          [t('accountingReports.amount')]: formatNumber(netInvesting),
        });
      }
      pushSection(t('accountingReports.financingActivities'), financingDetails);
      if (Number.isFinite(netFinancing)) {
        excelRows.push({
          [t('accountingReports.description')]: t('accountingReports.netCashFlow'),
          [t('accountingReports.amount')]: formatNumber(netFinancing),
        });
      }
      pushSection(t('accountingReports.otherActivities'), otherActivities);

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Cash Flow Statement');
      XLSX.writeFile(workbook, `CashFlowStatement_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success(t('accountingReports.exported', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
    } catch (error) {
      toast.error(error.message || t('accountingReports.exportError', { defaultValue: 'فشل في تصدير البيانات' }), { rtl: isRTL });
    }
  };

  const formatNumber = (value) =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    );
  };

  const reportData = cashFlowStatement?.data || cashFlowStatement || {};
  const summary = reportData?.summary || {};
  const totals = reportData?.totals || {};
  const meta = reportData?.meta || {};

  const openingCash =
    summary.cash_at_beginning ??
    totals.opening_cash ??
    totals.opening ??
    reportData.opening_cash ??
    0;
  const netChange =
    summary.net_change_in_cash ??
    totals.net_change ??
    totals.net_cash_flow ??
    reportData.net_change ??
    0;
  const closingCash =
    summary.cash_at_end ??
    totals.closing_cash ??
    totals.closing ??
    reportData.closing_cash ??
    0;

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);
  const mapDetails = (details) =>
    normalizeArray(details).map((item) => ({
      description: item.description || item.name || item.title || item.key || '—',
      amount: Number(item.amount ?? item.value ?? item.total ?? 0),
    }));
  const mapObject = (obj, exclude = []) =>
    Object.entries(obj || {})
      .filter(([key]) => !exclude.includes(key))
      .map(([key, value]) => ({
        description: t(`accountingReports.${key}`, {
          defaultValue: key.replace(/_/g, ' '),
        }),
        amount: Number(value ?? 0),
      }));

  const operatingObj =
    reportData.operating_activities ||
    reportData.operatingActivities ||
    reportData.operating ||
    reportData.operating_flows ||
    reportData.operatingFlows ||
    {};
  const investingObj =
    reportData.investing_activities ||
    reportData.investingActivities ||
    reportData.investing ||
    reportData.investing_flows ||
    reportData.investingFlows ||
    {};
  const financingObj =
    reportData.financing_activities ||
    reportData.financingActivities ||
    reportData.financing ||
    reportData.financing_flows ||
    reportData.financingFlows ||
    {};

  const operatingDetails =
    mapDetails(operatingObj.details).length > 0
      ? mapDetails(operatingObj.details)
      : mapObject(operatingObj, ['net_cash_from_operating', 'details']);
  const investingDetails =
    mapDetails(investingObj.details).length > 0
      ? mapDetails(investingObj.details)
      : mapObject(investingObj, ['net_cash_from_investing', 'details']);
  const financingDetails =
    mapDetails(financingObj.details).length > 0
      ? mapDetails(financingObj.details)
      : mapObject(financingObj, ['net_cash_from_financing', 'details']);

  const netOperating = Number(operatingObj.net_cash_from_operating ?? 0);
  const netInvesting = Number(investingObj.net_cash_from_investing ?? 0);
  const netFinancing = Number(financingObj.net_cash_from_financing ?? 0);
  const otherActivities = normalizeArray(reportData.other_flows || reportData.otherFlows || reportData.data || reportData.entries).map((item) => ({
    description: item.description || item.name || item.title || item.key || '—',
    amount: Number(item.amount ?? item.value ?? item.total ?? 0),
  }));



  return (
    <div style={{ padding: '20px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{
        backgroundColor: '#1b3350',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #333'
      }}>
        <h2 style={{ color: 'white', marginBottom: '20px', direction: dir, textAlign }}>
          {t('accountingReports.cashFlowStatement', { defaultValue: 'قائمة التدفقات النقدية' })}
        </h2>

        {/* Filters */}
        <form onSubmit={handleSubmit} style={{
          backgroundColor: '#1a1f2e',
          padding: '20px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.startDate', { defaultValue: 'تاريخ البداية' })}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
            <div>
              <label style={{ color: '#ccc', fontSize: '14px', display: 'block', marginBottom: '8px', direction: dir, textAlign }}>
                {t('accountingReports.endDate', { defaultValue: 'تاريخ النهاية' })}
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#14263b',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '16px'
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#666' : '#AC2000',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                opacity: isLoading ? 0.6 : 1,
                minWidth: '180px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isLoading ? t('accountingReports.loading', { defaultValue: 'جاري التحميل...' }) : t('accountingReports.view', { defaultValue: 'عرض' })}
            </button>
          </div>
        </form>

        {/* Action Buttons */}
        {cashFlowStatement && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: '#444',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaEye /> {t('accountingReports.previewAllPDF', { defaultValue: 'عرض PDF للكل' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                justifyContent: 'center'
              }}
            >
              <FaFileExcel /> {t('accountingReports.exportExcel', { defaultValue: 'تصدير Excel للكل' })}
            </button>
          </div>
        )}

        {/* Report Data Display */}
        {cashFlowStatement && (
          <div style={{
            backgroundColor: '#1a1f2e',
            padding: '20px',
            borderRadius: '8px',
            marginTop: '20px'
          }}>
            <div style={{ color: '#ccc', marginBottom: '20px', direction: dir, textAlign }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('accountingReports.period')}:</strong> {formatDate(meta.start_date || startDate)} - {formatDate(meta.end_date || endDate)}
              </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px', marginBottom: '24px' }}>
              <div style={{ padding: '14px', backgroundColor: '#1b3350', borderRadius: '8px', border: '1px solid #333', direction: dir, textAlign }}>
                <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '6px' }}>
                  {t('accountingReports.openingCash')}
                </div>
                <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>{formatNumber(openingCash)}</div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#1b3350', borderRadius: '8px', border: '1px solid #333', direction: dir, textAlign }}>
                <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '6px' }}>
                  {t('accountingReports.netCashFlow')}
                </div>
                <div style={{ color: netChange >= 0 ? '#28a745' : '#dc3545', fontSize: '18px', fontWeight: 'bold' }}>
                  {formatNumber(netChange)}
                </div>
              </div>
              <div style={{ padding: '14px', backgroundColor: '#1b3350', borderRadius: '8px', border: '1px solid #333', direction: dir, textAlign }}>
                <div style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '6px' }}>
                  {t('accountingReports.closingCash')}
                </div>
                <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>{formatNumber(closingCash)}</div>
              </div>
            </div>

            {/* Sections Tables */}
            {[
              { title: t('accountingReports.operatingActivities'), rows: operatingDetails, net: netOperating },
              { title: t('accountingReports.investingActivities'), rows: investingDetails, net: netInvesting },
              { title: t('accountingReports.financingActivities'), rows: financingDetails, net: netFinancing },
              { title: t('accountingReports.otherActivities'), rows: otherActivities, net: null },
            ].map(({ title, rows, net }) => (
              rows.length > 0 && (
                <div key={title} style={{ marginBottom: '20px' }}>
                  <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '16px', direction: dir, textAlign }}>{title}</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#1b3350' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#AC2000' }}>
                          <th style={{ padding: '12px', textAlign, direction: dir, color: 'white', border: '1px solid #333' }}>
                            {t('accountingReports.description', { defaultValue: 'الوصف' })}
                          </th>
                          <th style={{ padding: '12px', textAlign: 'center', color: 'white', border: '1px solid #333' }}>
                            {t('accountingReports.amount', { defaultValue: 'القيمة' })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((item, idx) => (
                          <tr key={item.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e' }}>
                            <td style={{ padding: '12px', textAlign, direction: dir, color: 'white', border: '1px solid #333' }}>
                              {item.description || '—'}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#28a745' : '#dc3545', border: '1px solid #333' }}>
                              {formatNumber(item.amount ?? 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {Number.isFinite(net) && (
                        <tfoot>
                          <tr style={{ backgroundColor: '#111827' }}>
                            <td style={{ padding: '12px', textAlign, direction: dir, color: '#ccc', border: '1px solid #333' }}>
                              {t('accountingReports.netCashFlow')}
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center', color: net >= 0 ? '#28a745' : '#dc3545', border: '1px solid #333', fontWeight: 'bold' }}>
                              {formatNumber(net)}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* PDF Print Templates */}
      {pdfType === 'all' && cashFlowStatement && pdfChunks && pdfChunks.length > 0 && (
        pdfChunks.map((page, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`cash-flow-statement-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 0,
              width: '794px',
              minHeight: '1123px',
              height: 'auto',
              backgroundColor: '#ffffff',
              color: '#000000',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              padding: '40px',
              boxSizing: 'border-box',
              overflow: 'visible'
            }}
          >
            <div style={{
              border: '5px solid #000',
              padding: '3px',
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              width: '100%',
              minHeight: '1043px',
              height: 'auto',
              overflow: 'visible'
            }}>
              <div style={{
                border: '2px solid #000',
                minHeight: '1033px',
                height: '1033px',
                padding: '18px 18px 10px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                direction: dir,
                backgroundColor: '#ffffff',
                boxSizing: 'border-box',
                width: '100%',
                overflow: 'visible'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '600px',
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: '95%',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  opacity: 0.1,
                  zIndex: 0,
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      marginBottom: '8px',
                      borderBottom: '4px double #000',
                      paddingBottom: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                    <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                        {t('accountingReports.cashFlowStatementTitle')}
                      </h2>
                      <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                        {formatDate(meta.start_date || startDate)} - {formatDate(meta.end_date || endDate)}
                      </div>
                    </div>

                    {/* Summary - Show only on first page */}
                    {page.showSummary && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '18px' }}>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.openingCash')}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700' }}>{formatNumber(openingCash)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.netCashFlow')}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: netChange >= 0 ? '#0f5132' : '#b22222' }}>{formatNumber(netChange)}</div>
                        </div>
                        <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                          <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.closingCash')}</div>
                          <div style={{ fontSize: '14px', fontWeight: '700' }}>{formatNumber(closingCash)}</div>
                        </div>
                      </div>
                    )}

                    {/* Operating Activities */}
                    {page.operating && page.operating.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <h3 style={{ fontSize: '15px', marginBottom: '8px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.operatingActivities')}</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.operating.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(item.amount ?? 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {page.isOperatingLastChunk && Number.isFinite(page.netOperating) && (
                            <tfoot>
                              <tr>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                  {t('accountingReports.netCashFlow')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netOperating >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(page.netOperating)}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}

                    {/* Financing Activities */}
                    {page.financing && page.financing.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <h3 style={{ fontSize: '15px', marginBottom: '8px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.financingActivities')}</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.financing.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(item.amount ?? 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {page.isFinancingLastChunk && Number.isFinite(page.netFinancing) && (
                            <tfoot>
                              <tr>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                  {t('accountingReports.netCashFlow')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netFinancing >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(page.netFinancing)}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}

                    {/* Investing Activities */}
                    {page.investing && page.investing.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <h3 style={{ fontSize: '15px', marginBottom: '8px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.investingActivities')}</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.investing.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(item.amount ?? 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {page.isInvestingLastChunk && Number.isFinite(page.netInvesting) && (
                            <tfoot>
                              <tr>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                  {t('accountingReports.netCashFlow')}
                                </td>
                                <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netInvesting >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(page.netInvesting)}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    )}

                    {/* Other Activities */}
                    {page.other && page.other.length > 0 && (
                      <div style={{ marginBottom: '14px' }}>
                        <h3 style={{ fontSize: '15px', marginBottom: '8px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.otherActivities')}</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                              <th style={{ border: '1px solid #000', padding: '8px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {page.other.map((item, idx) => (
                              <tr key={item.id || idx}>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                  {formatNumber(item.amount ?? 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div style={{
                    position: 'absolute',
                    bottom: '10px',
                    left: '18px',
                    right: '18px',
                    flexShrink: 0
                  }}>
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'contain',
                        borderTop: '4px double #000',
                        paddingTop: '8px',
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      )}

      {/* Preview Modal */}
      {isPreviewOpen && cashFlowStatement && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            padding: '20px',
            overflow: 'hidden'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsPreviewOpen(false);
            }
          }}
          onWheel={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setPreviewZoom((prev) => Math.max(0.3, Math.min(2, prev + delta)));
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#1a1f2e',
              borderRadius: '12px',
              padding: '20px',
              width: '100%',
              height: '100%',
              margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
              <h2 style={{ color: 'white', margin: 0, fontSize: '20px', direction: dir, textAlign }}>
                {t('accountingReports.cashFlowStatementTitle')}
              </h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginRight: '10px' }}>
                  <button
                    onClick={() => setPreviewZoom((prev) => Math.max(0.3, prev - 0.1))}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                    title={t('accountingReports.zoomOut', { defaultValue: 'تصغير' })}
                  >
                    −
                  </button>
                  <span style={{ color: 'white', minWidth: '60px', textAlign: 'center', fontSize: '14px' }}>
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setPreviewZoom((prev) => Math.min(2, prev + 0.1))}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}
                    title={t('accountingReports.zoomIn', { defaultValue: 'تكبير' })}
                  >
                    +
                  </button>
                  <button
                    onClick={() => {
                      setPreviewZoom(1.0);
                      setPreviewPosition({ x: 0, y: 0 });
                    }}
                    style={{
                      backgroundColor: '#444',
                      color: 'white',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={t('accountingReports.fit', { defaultValue: 'ملاءمة الشاشة' })}
                  >
                    {t('accountingReports.fit', { defaultValue: 'ملاءمة' })}
                  </button>
                </div>
                <button
                  onClick={handlePrintAll}
                  disabled={isGeneratingPdf}
                  style={{
                    backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: isGeneratingPdf ? 0.6 : 1
                  }}
                >
                  <FaPrint /> {isGeneratingPdf ? t('accountingReports.generating', { defaultValue: 'جاري التوليد...' }) : t('accountingReports.printAllPDF', { defaultValue: 'طباعة PDF للكل' })}
                </button>
                <button
                  onClick={() => setIsPreviewOpen(false)}
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 20px',
                    cursor: 'pointer'
                  }}
                >
                  {t('accountingReports.close', { defaultValue: 'إغلاق' })}
                </button>
              </div>
            </div>
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '20px',
                overflow: previewZoom <= 1.0 ? 'auto' : 'hidden',
                flex: 1,
                display: 'flex',
                alignItems: previewZoom <= 1.0 ? 'flex-start' : 'center',
                justifyContent: 'center',
                minHeight: 0,
                position: 'relative',
                cursor: previewZoom > 1.0 && isDragging ? 'grabbing' : previewZoom > 1.0 ? 'grab' : 'default'
              }}
              onMouseDown={(e) => {
                if (previewZoom > 1.0 && e.button === 0) {
                  setIsDragging(true);
                  setDragStart({
                    x: e.clientX - previewPosition.x,
                    y: e.clientY - previewPosition.y
                  });
                }
              }}
              onDragStart={(e) => previewZoom > 1.0 && e.preventDefault()}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                {pdfChunks && pdfChunks.length > 0 ? (
                  pdfChunks.map((page, chunkIndex) => (
                    <div
                      key={chunkIndex}
                      style={{
                        transform: previewZoom > 1.0
                          ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                          : `scale(${previewZoom})`,
                        transformOrigin: previewZoom <= 1.0 ? 'top center' : 'center center',
                        transition: isDragging ? 'none' : 'transform 0.2s ease',
                        width: '794px',
                        userSelect: previewZoom > 1.0 ? 'none' : 'auto'
                      }}
                    >
                      <div style={{
                        border: '5px solid #000',
                        padding: '3px',
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box',
                        width: '794px',
                        minHeight: '1123px',
                        height: 'auto',
                        overflow: 'visible'
                      }}>
                        <div style={{
                          border: '2px solid #000',
                          minHeight: '1033px',
                          height: '1033px',
                          padding: '18px 18px 10px',
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                          fontWeight: 'bold',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          textRendering: 'optimizeLegibility',
                          direction: dir,
                          backgroundColor: '#ffffff',
                          boxSizing: 'border-box',
                          width: '100%',
                          overflow: 'visible'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${headerLogo})`,
                            backgroundSize: '95%',
                            backgroundPosition: 'center center',
                            backgroundRepeat: 'no-repeat',
                            opacity: 0.08,
                            zIndex: 0,
                            pointerEvents: 'none'
                          }} />
                          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                            <img
                              src={`${publicUrl}/HeaderAce.png`}
                              alt="Header"
                              style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                marginBottom: '8px',
                                borderBottom: '4px double #000',
                                paddingBottom: '8px',
                                flexShrink: 0
                              }}
                              crossOrigin="anonymous"
                            />

                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px', direction: dir }}>
                                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0, direction: dir }}>
                                  {t('accountingReports.cashFlowStatementTitle')}
                                </h2>
                                <div style={{ fontSize: '14px', marginTop: '8px', direction: dir }}>
                                  {formatDate(meta.start_date || startDate)} - {formatDate(meta.end_date || endDate)}
                                </div>
                              </div>
                              {/* Report content */}
                              <div style={{ color: '#000', fontSize: '14px' }}>
                                {/* Summary - Show only on first page */}
                                {page.showSummary && (
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                                    <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.openingCash')}</div>
                                      <div style={{ fontSize: '14px', fontWeight: '700' }}>{formatNumber(openingCash)}</div>
                                    </div>
                                    <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.netCashFlow')}</div>
                                      <div style={{ fontSize: '14px', fontWeight: '700', color: netChange >= 0 ? '#0f5132' : '#b22222' }}>{formatNumber(netChange)}</div>
                                    </div>
                                    <div style={{ padding: '10px', border: '1px solid #000', borderRadius: '4px', backgroundColor: '#f5f5f5', direction: dir, textAlign }}>
                                      <div style={{ fontSize: '12px', marginBottom: '4px' }}>{t('accountingReports.closingCash')}</div>
                                      <div style={{ fontSize: '14px', fontWeight: '700' }}>{formatNumber(closingCash)}</div>
                                    </div>
                                  </div>
                                )}

                                {/* Operating Activities */}
                                {page.operating && page.operating.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <h3 style={{ fontSize: '14px', marginBottom: '6px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.operatingActivities')}</h3>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {page.operating.map((item, idx) => (
                                          <tr key={item.id || idx}>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(item.amount ?? 0)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      {page.isOperatingLastChunk && Number.isFinite(page.netOperating) && (
                                        <tfoot>
                                          <tr>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                              {t('accountingReports.netCashFlow')}
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netOperating >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(page.netOperating)}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      )}
                                    </table>
                                  </div>
                                )}

                                {/* Financing Activities */}
                                {page.financing && page.financing.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <h3 style={{ fontSize: '14px', marginBottom: '6px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.financingActivities')}</h3>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {page.financing.map((item, idx) => (
                                          <tr key={item.id || idx}>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(item.amount ?? 0)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      {page.isFinancingLastChunk && Number.isFinite(page.netFinancing) && (
                                        <tfoot>
                                          <tr>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                              {t('accountingReports.netCashFlow')}
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netFinancing >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(page.netFinancing)}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      )}
                                    </table>
                                  </div>
                                )}

                                {/* Investing Activities */}
                                {page.investing && page.investing.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <h3 style={{ fontSize: '14px', marginBottom: '6px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.investingActivities')}</h3>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {page.investing.map((item, idx) => (
                                          <tr key={item.id || idx}>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(item.amount ?? 0)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      {page.isInvestingLastChunk && Number.isFinite(page.netInvesting) && (
                                        <tfoot>
                                          <tr>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign, direction: dir, backgroundColor: '#f5f5f5' }}>
                                              {t('accountingReports.netCashFlow')}
                                            </td>
                                            <td style={{ border: '1px solid #000', padding: '7px', textAlign: 'center', backgroundColor: '#f5f5f5', fontWeight: '700', color: page.netInvesting >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(page.netInvesting)}
                                            </td>
                                          </tr>
                                        </tfoot>
                                      )}
                                    </table>
                                  </div>
                                )}

                                {/* Other Activities */}
                                {page.other && page.other.length > 0 && (
                                  <div style={{ marginBottom: '12px' }}>
                                    <h3 style={{ fontSize: '14px', marginBottom: '6px', color: '#000080', direction: dir, textAlign }}>{t('accountingReports.otherActivities')}</h3>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign, direction: dir }}>{t('accountingReports.description')}</th>
                                          <th style={{ border: '1px solid #000', padding: '7px', backgroundColor: '#e5e7eb', textAlign: 'center' }}>{t('accountingReports.amount')}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {page.other.map((item, idx) => (
                                          <tr key={item.id || idx}>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign, direction: dir }}>{item.description || '—'}</td>
                                            <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', color: (item.amount ?? 0) >= 0 ? '#0f5132' : '#b22222' }}>
                                              {formatNumber(item.amount ?? 0)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div style={{
                              position: 'absolute',
                              bottom: '10px',
                              left: '18px',
                              right: '18px',
                              flexShrink: 0
                            }}>
                              <img
                                src={`${publicUrl}/FooterAce.png`}
                                alt="Footer"
                                style={{
                                  width: '100%',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  borderTop: '4px double #000',
                                  paddingTop: '8px',
                                }}
                                crossOrigin="anonymous"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="cash-flow-statement-pdf-measure-operating-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>{t('accountingReports.cashFlowStatementTitle')}</h2>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>{formatDate(meta.start_date || startDate)} - {formatDate(meta.end_date || endDate)}</div>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="cash-flow-statement-pdf-measure-operating-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.amount')}</th>
            </tr>
          </thead>
          <tbody id="cash-flow-statement-pdf-measure-operating-tbody">
            {operatingDetails.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.amount ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="cash-flow-statement-pdf-measure-financing-top-header" style={{ width: '100%' }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="cash-flow-statement-pdf-measure-financing-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.amount')}</th>
            </tr>
          </thead>
          <tbody id="cash-flow-statement-pdf-measure-financing-tbody">
            {financingDetails.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.amount ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="cash-flow-statement-pdf-measure-investing-top-header" style={{ width: '100%' }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="cash-flow-statement-pdf-measure-investing-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.amount')}</th>
            </tr>
          </thead>
          <tbody id="cash-flow-statement-pdf-measure-investing-tbody">
            {investingDetails.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.amount ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div id="cash-flow-statement-pdf-measure-other-top-header" style={{ width: '100%' }} />
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000' }}>
          <thead id="cash-flow-statement-pdf-measure-other-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.description')}</th>
              <th style={{ border: '1px solid #000', padding: '6px' }}>{t('accountingReports.amount')}</th>
            </tr>
          </thead>
          <tbody id="cash-flow-statement-pdf-measure-other-tbody">
            {otherActivities.slice(0, 5).map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{item.description || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px' }}>{formatNumber(item.amount ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="cash-flow-statement-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

export default CashFlowStatement;
